/* ==========================================================================
   PWR PRODUCT CART — Phase 2A purchase-engine cleanup
   --------------------------------------------------------------------------
   Product-page-scoped only. Loaded exclusively from sections/main-product
   .liquid. Two responsibilities:

   1. Define the <cart-notification> custom element. Dawn's stock
      assets/cart-notification.js is not loaded anywhere in this theme (only
      referenced by the unused stock sections/header.liquid), so there is no
      naming/registration conflict defining it here. This copy matches stock
      behavior except: (a) it guards each section update with an existence
      check before writing innerHTML, and (b) it deliberately excludes
      'cart-icon-bubble' from getSectionsToRender() — this theme's header
      icon/count markup is custom (sections/pwr-header.liquid), and writing
      Dawn's stock bubble fragment into it here would destroy that markup.
      The header count is instead kept in sync independently via
      PWR.utils.syncCartCount() below (P0-1 fix, Aug 2026).

   2. Keep the mobile sticky Add-to-Cart bar's price and availability display
      in sync with the real product form, using Dawn's own
      PUB_SUB_EVENTS.variantChange (published by stock product-info.js) —
      no separate money-formatting or variant logic invented here, this only
      copies already-correct text/state from the real price block and the
      real submit button.

   Depends only on globals already loaded sitewide by layout/theme.liquid
   (subscribe, PUB_SUB_EVENTS) and stock cart-notification.js. Does not
   modify base.css, pwr-design-system.css, pwr-components.css,
   pwr-product-card.liquid, pwr-product-actions.liquid, or any homepage/
   collection file.
   ========================================================================== */
(function () {
  'use strict';

  if (!customElements.get('cart-notification')) {
    customElements.define(
      'cart-notification',
      class PwrCartNotification extends HTMLElement {
        constructor() {
          super();

          this.notification = document.getElementById('cart-notification');
          if (!this.notification) return;

          this.header = document.querySelector('sticky-header');
          this.onBodyClick = this.handleBodyClick.bind(this);

          this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
            closeButton.addEventListener('click', this.close.bind(this))
          );
        }

        open() {
          this.notification.classList.add('animate', 'active');

          this.notification.addEventListener(
            'transitionend',
            () => {
              this.notification.focus();
              if (typeof trapFocus === 'function') trapFocus(this.notification);
            },
            { once: true }
          );

          document.body.addEventListener('click', this.onBodyClick);
        }

        close() {
          this.notification.classList.remove('active');
          document.body.removeEventListener('click', this.onBodyClick);

          if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
        }

        renderContents(parsedState) {
          this.cartItemKey = parsedState.key;
          this.getSectionsToRender().forEach((section) => {
            const target = document.getElementById(section.id);
            // Defensive: stock Dawn assumes every requested section id has a
            // matching element in the DOM. This theme's live header cannot
            // guarantee that for cart-icon-bubble (see file header comment) —
            // skip rather than throw so the notification still opens.
            if (!target) return;
            target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          });

          if (window.PWR && window.PWR.utils) window.PWR.utils.syncCartCount();
          if (this.header) this.header.reveal();
          this.open();
        }

        getSectionsToRender() {
          return [
            {
              id: 'cart-notification-product',
              selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
            },
            {
              id: 'cart-notification-button',
            },
          ];
        }

        getSectionInnerHTML(html, selector = '.shopify-section') {
          const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
          return parsed ? parsed.innerHTML : '';
        }

        handleBodyClick(evt) {
          const target = evt.target;
          if (target !== this.notification && !target.closest('cart-notification')) {
            const disclosure = target.closest('details-disclosure, header-menu');
            this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
            this.close();
          }
        }

        setActiveElement(element) {
          this.activeElement = element;
        }
      }
    );
  }

  function initStickySync() {
    const stickyPrice = document.querySelector('[data-pwr-sticky-price]');
    const stickySubmit = document.querySelector('[data-pwr-sticky-submit]');
    if (!stickyPrice && !stickySubmit) return;

    function syncFromSectionId(sectionId) {
      if (stickyPrice) {
        const priceWrap = document.getElementById('price-' + sectionId);
        const amountEl =
          priceWrap && (priceWrap.querySelector('.price-item--sale') || priceWrap.querySelector('.price-item--regular'));
        if (amountEl) stickyPrice.textContent = amountEl.textContent.trim();
      }

      if (stickySubmit) {
        const mainSubmit = document.getElementById('ProductSubmitButton-' + sectionId);
        const soldOut = !mainSubmit || mainSubmit.hasAttribute('disabled');
        stickySubmit.disabled = soldOut;
        stickySubmit.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      }
    }

    // Reflect the state already rendered at page load (e.g. sold out on
    // first paint), then stay in sync as the shopper changes variant.
    const productInfo = document.querySelector('product-info[data-section]');
    if (productInfo) syncFromSectionId(productInfo.dataset.section);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event && event.data && event.data.sectionId) syncFromSectionId(event.data.sectionId);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickySync);
  } else {
    initStickySync();
  }
})();
